import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { SocialBet } from 'src/types/domain/SocialBet'
import type { BetStatus } from 'src/types/domain/BetStatus'
import { SOCIAL_CONFIG } from 'src/config'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

setAutoFreeze(false)

export interface SocialState {
  raceId?: string
  gameId?: string
  bets: SocialBet[]
  /**
   * ⚠️ DEAD — nothing reads this any more.
   *
   * Its only consumer was `SocialList`, which stopped being rendered when
   * every game moved to the crash-style feed. The new panels read `allBets`,
   * UNCAPPED — removing the 10-row `SOCIAL_CONFIG.TOP_BETS_LIMITER` cap was
   * part of the point, since it meant an 11th player was never shown.
   *
   * `updateTopBets` still runs on every bet insert, so this is a small amount
   * of wasted work per bet, not a correctness problem. Deleting it means
   * deleting SocialList too — its own change, its own risk. Cleanup ticket.
   */
  topBets: SocialBet[]
  totalBets: number
  totalAmount: number
  /** Persistent registry of all bets for the current round, keyed by bet ID */
  allBets: Record<string, SocialBet>
  /** Bets from the previous round (for "Previous" tab), keyed by bet ID */
  previousBets: Record<string, SocialBet>
  /** Round ID of the previous round */
  previousRaceId?: string
  /** Lightning config remembered per-round so bets that arrive AFTER the
   *  reveal (page-reload race condition) can be boosted on insert by
   *  betReceived / setRoundSnapshot. */
  lightningRoundConfigs: Record<string, { items: string[]; percent: number }>
  /** Per-bet idempotency for the boost so the bulk apply can run
   *  multiple times safely (handleLightningRevealed fires once,
   *  useGameListeners backstop also fires) without double-boosting. */
  boostedBetIds: Record<string, true>
  /** Bets broadcast for a round the CLIENT doesn't know yet (v49). The
   *  socket delivers new-round bets faster than the round row propagates
   *  to this client (Supabase realtime + mobile radio), so the busiest
   *  slice of traffic — auto-rebets at betting open — used to be dropped
   *  by a `raceId === bet.raceId` gate against the client's lagging view.
   *  Now they park here (with the round's latest server totals) and drain
   *  the moment raceChanged flips to their round. Pruned to the newest
   *  few rounds so a stale id can never grow unbounded. */
  pendingByRound: Record<
    string,
    { bets: SocialBet[]; totalBets?: number; totalAmount?: number }
  >
}

export interface SocialActions {
  /** Insert a live-broadcast bet. The STORE owns round matching (v49):
   *  current round → insert (+ the broadcast's round totals), previous
   *  round → straggler into the Previous tab, unknown round → buffered
   *  until raceChanged drains it. Never silently dropped. */
  betReceived: (bet: SocialBet, roundTotals?: { totalBets: number; totalAmount: number }) => void
  betProcessed: (processedBetId: string) => void
  gameChanged: (gameId: string, raceId: string) => void
  /** opts.fromRaceCreated: set ONLY by raceAdded for socket-fresh
   *  `race:created` ids — unlocks the snapshot-misclassification promotion.
   *  Fetch-derived callers (setCurrentGame) must omit it. */
  raceChanged: (raceId: string, opts?: { fromRaceCreated?: boolean }) => void
  setTotalBets: (totalBets: number) => void
  setTotalAmount: (totalAmount: number) => void
  removeUnprocessedBets: () => void
  /** Replace allBets with a snapshot from the server (on join/reconnect) */
  setRoundSnapshot: (bets: SocialBet[]) => void
  /** Update a bet's status and outcome fields (on bet:won or bet:cashed_out) */
  /**
   * `fallbackBet` is the full record the server sends on every outcome. When
   * this client never saw the bet's original `bet` broadcast, it is upserted
   * from here rather than dropped — otherwise the row stays missing AND the
   * winners count and total-won under-report.
   */
  updateBetStatus: (
    betId: string,
    status: BetStatus,
    cashoutMultiplier?: number,
    actualPayout?: number,
    fallbackBet?: SocialBet
  ) => void
  /** Reverse the pre-reveal `potentialPayout` strip applied to chip-placement
   *  bets so the social feed shows boosted multipliers once lightning lands. */
  applyLightningBoost: (
    raceId: string,
    boostedItemIds: string[],
    boostPercent: number
  ) => void
}

type SocialStore = SocialState & { actions: SocialActions }

export const useSocialStore = create<SocialStore>()(
  devtools(
    immer((set) => ({
      bets: [],
      topBets: [],
      raceId: undefined,
      gameId: undefined,
      totalBets: 0,
      totalAmount: 0,
      allBets: {},
      previousBets: {},
      previousRaceId: undefined,
      lightningRoundConfigs: {},
      boostedBetIds: {},
      pendingByRound: {},
      actions: {
        betReceived: (
          bet: SocialBet,
          roundTotals?: { totalBets: number; totalAmount: number }
        ) => {
          set(
            (state) => {
              // Current round (or the store hasn't learned ANY round yet —
              // treat as current rather than lose it): insert + totals.
              if (!state.raceId || bet.raceId === state.raceId) {
                insertBet(state, bet)
                if (roundTotals) {
                  state.totalBets = roundTotals.totalBets
                  state.totalAmount = roundTotals.totalAmount
                }
                return
              }
              // Straggler for the round that just rotated out: land it in
              // the Previous tab instead of vanishing.
              if (bet.raceId === state.previousRaceId) {
                if (!state.previousBets[bet.id]) state.previousBets[bet.id] = bet
                return
              }
              // A round this client doesn't know yet (v49): buffer until
              // raceChanged flips to it. Keep the round's LATEST server
              // totals alongside (they're cumulative snapshots).
              const slot = (state.pendingByRound[bet.raceId] ??= { bets: [] })
              if (!slot.bets.some((b) => b.id === bet.id)) slot.bets.push(bet)
              if (roundTotals) {
                slot.totalBets = roundTotals.totalBets
                slot.totalAmount = roundTotals.totalAmount
              }
              prunePending(state)
            },
            undefined,
            'betReceived'
          )
        },
        betProcessed: (processedBetId: string) => {
          set(
            (state) => {
              state.bets = state.bets.filter((bet) => bet.id !== processedBetId)
            },
            undefined,
            'betProcessed'
          )
        },
        removeUnprocessedBets: () => {
          set(
            (state) => {
              state.bets = []
            },
            undefined,
            'removeUnprocessedBets'
          )
        },
        gameChanged: (gameId: string, raceId: string) => {
          set(
            (state) => {
              // Set new game/race ID
              state.gameId = gameId
              state.raceId = raceId
              resetBets(state)
            },
            undefined,
            'gameChanged'
          )
        },
        raceChanged: (raceId: string, opts?: { fromRaceCreated?: boolean }) => {
          const fromRaceCreated = opts?.fromRaceCreated === true

          set(
            (state) => {
              // Idempotency guard (fixes "shows 1 of my 2 bets"): rounds only
              // move FORWARD, so a notification for the round we're already on
              // (state.raceId) or the one we just left (previousRaceId) is
              // spurious — a re-delivered `race:created` re-firing the
              // unguarded raceAdded→raceChanged, or a lagged setCurrentGame
              // fetch whose races[0] trails the socket. Rotating on it would
              // wipe this round's already-collected bets into the Previous tab
              // and only the echoes arriving AFTER the wipe repopulate the feed
              // — the drop the player sees. Only a genuine forward round change
              // proceeds past here.
              if (raceId === state.raceId) {
                return
              }

              if (raceId === state.previousRaceId) {
                // The round we hold as "previous" is being declared CURRENT.
                // When the current round has NO bets and previous does, the
                // likeliest truth is setRoundSnapshot misclassified the LIVE
                // round as previous (the store's raceId was stale from a
                // lagging fetch on join) — PROMOTE it back to current instead
                // of dropping the event, so the feed shows the live round's
                // bets on the Current tab.
                //
                // PROVENANCE GATE (adversarial-review fix): promotion is only
                // trusted for socket-fresh `race:created` notifications
                // (raceAdded's dedup guarantees the id was never seen before,
                // so previousRaceId equality can only mean misclassification).
                // A fetch-derived call (setCurrentGame refetch on tab refocus,
                // read off the replica one round behind at a transition) hits
                // the exact same preconditions with a genuinely FINISHED round
                // — promoting there would roll the live round back. Those
                // callers stay on the plain no-op guard.
                //
                // With any current bets collected this is a strict no-op:
                // promotion must never wipe a round that is genuinely in
                // progress (the #652 guarantee).
                if (
                  fromRaceCreated &&
                  Object.keys(state.allBets).length === 0 &&
                  Object.keys(state.previousBets).length > 0
                ) {
                  state.raceId = raceId
                  state.allBets = { ...state.previousBets }
                  state.previousBets = {}
                  state.previousRaceId = undefined
                  state.bets = []
                  state.topBets = []
                  for (const bet of Object.values(state.allBets)) {
                    updateTopBets(bet)(state)
                  }
                  state.totalBets = Math.max(
                    state.totalBets,
                    Object.keys(state.allBets).length
                  )

                  // DRAIN the buffer for the promoted round, don't drop it:
                  // bets broadcast between the channel join and the snapshot
                  // landing were parked in pendingByRound (the store's raceId
                  // was stale, previousRaceId not yet set) and are NOT in the
                  // snapshot — exactly the auto-rebets-at-open the v49 buffer
                  // exists to save. Mirrors the forward-rotation drain.
                  const buffered = state.pendingByRound[raceId]
                  if (buffered) {
                    for (const bet of buffered.bets) insertBet(state, bet)
                    if (buffered.totalBets !== undefined) state.totalBets = buffered.totalBets
                    if (buffered.totalAmount !== undefined) {
                      state.totalAmount = buffered.totalAmount
                    }
                    delete state.pendingByRound[raceId]
                  }
                }

                return
              }
              // Save current round bets as previous before resetting
              if (Object.keys(state.allBets).length > 0) {
                state.previousBets = { ...state.allBets }
                state.previousRaceId = state.raceId
              }
              // Set new race ID and reset current round data
              state.raceId = raceId
              state.bets = []
              state.topBets = []
              state.totalBets = 0
              state.totalAmount = 0
              state.allBets = {}

              // v49 — drain bets that arrived for this round BEFORE the
              // client learned it existed (the socket beats the round row
              // to this client on every transition; auto-rebets fire in
              // exactly that window).
              const buffered = state.pendingByRound[raceId]
              if (buffered) {
                for (const bet of buffered.bets) insertBet(state, bet)
                if (buffered.totalBets !== undefined) state.totalBets = buffered.totalBets
                if (buffered.totalAmount !== undefined) state.totalAmount = buffered.totalAmount
                delete state.pendingByRound[raceId]
              }
              prunePending(state)
            },
            undefined,
            'raceChanged'
          )
        },
        setTotalBets: (totalBets: number) => {
          set(
            (state) => {
              state.totalBets = totalBets
            },
            undefined,
            'setTotalBets'
          )
        },
        setTotalAmount: (totalAmount: number) => {
          set(
            (state) => {
              state.totalAmount = totalAmount
            },
            undefined,
            'setTotalAmount'
          )
        },
        setRoundSnapshot: (bets: SocialBet[]) => {
          set(
            (state) => {
              const currentBets: Record<string, SocialBet> = {}

              // Group non-current bets by raceId to find the single most recent previous round
              const betsByRace: Record<string, SocialBet[]> = {}

              for (const bet of bets) {
                if (state.raceId && bet.raceId === state.raceId) {
                  currentBets[bet.id] = bet
                } else if (bet.raceId) {
                  if (!betsByRace[bet.raceId]) betsByRace[bet.raceId] = []
                  betsByRace[bet.raceId]!.push(bet)
                }
              }

              // Pick the most recent previous round (highest createdAt among its bets)
              let latestPrevRaceId: string | undefined
              let latestTimestamp = ''
              for (const [raceId, raceBets] of Object.entries(betsByRace)) {
                const maxTs = raceBets.reduce(
                  (max, b) => (b.createdAt > max ? b.createdAt : max),
                  ''
                )
                if (maxTs > latestTimestamp) {
                  latestTimestamp = maxTs
                  latestPrevRaceId = raceId
                }
              }

              // v49 — UNION, not replace: a bet broadcast in the gap
              // between channel join and this snapshot landing was already
              // inserted live; a plain replace wiped it (the server built
              // the snapshot before that bet existed).
              for (const [id, existing] of Object.entries(state.allBets)) {
                if (!currentBets[id]) currentBets[id] = existing
              }
              state.allBets = currentBets
              state.totalBets = Math.max(state.totalBets, Object.keys(currentBets).length)

              // Only update previousBets from snapshot if we don't already have them
              // (they may have been set by raceChanged)
              if (latestPrevRaceId && Object.keys(state.previousBets).length === 0) {
                const prevBets: Record<string, SocialBet> = {}
                for (const bet of betsByRace[latestPrevRaceId]!) {
                  prevBets[bet.id] = bet
                }
                state.previousBets = prevBets
                state.previousRaceId = latestPrevRaceId
              }
            },
            undefined,
            'setRoundSnapshot'
          )
        },
        updateBetStatus: (
          betId: string,
          status: BetStatus,
          cashoutMultiplier?: number,
          actualPayout?: number,
          fallbackBet?: SocialBet
        ) => {
          set(
            (state) => {
              // Check current round bets
              const bet = state.allBets[betId]
              if (bet) {
                bet.status = status
                if (cashoutMultiplier !== undefined) bet.cashoutMultiplier = cashoutMultiplier
                if (actualPayout !== undefined) bet.actualPayout = actualPayout
                return
              }
              // Check previous round bets (outcomes may arrive after round transition)
              const prevBet = state.previousBets[betId]
              if (prevBet) {
                prevBet.status = status
                if (cashoutMultiplier !== undefined) prevBet.cashoutMultiplier = cashoutMultiplier
                if (actualPayout !== undefined) prevBet.actualPayout = actualPayout
                return
              }

              // We never saw this bet. UPSERT rather than drop it.
              //
              // The server always sends a FULL bet record on bet:won /
              // bet:cashed_out — it even rebuilds a missing Mnesia row to do
              // so. Whenever the original `bet` broadcast was lost (a cluster
              // island during a deploy, the join-time snapshot race, a
              // terminal channel close) this was the one repair signal
              // guaranteed to arrive, and returning here threw it away: the
              // row never appeared, and the winners count and total-won
              // under-reported on top of it.
              if (!fallbackBet) return

              const resolved: SocialBet = {
                ...fallbackBet,
                status,
                ...(cashoutMultiplier !== undefined ? { cashoutMultiplier } : {}),
                ...(actualPayout !== undefined ? { actualPayout } : {})
              }

              if (state.raceId && resolved.raceId === state.raceId) {
                insertBet(state, resolved)
                return
              }

              if (state.previousRaceId && resolved.raceId === state.previousRaceId) {
                state.previousBets[resolved.id] = resolved
                return
              }

              // Any older round has already rotated out of both tabs.
              // Inserting there would resurrect a dead round into the live
              // feed, which is worse than the missing row.
            },
            undefined,
            'updateBetStatus'
          )
        },
        // Reverses the chip-placement pre-reveal `potentialPayout` strip on
        // social-feed bets the moment lightning reveals. Same logic as the
        // bet store's equivalent action — pending bets on boosted marbles
        // get their potentialPayout multiplied by the lightning factor.
        // Outcome-set bets (won/cashed_out) get their boosted payout via
        // updateBetStatus's actualPayout path instead, so we only touch
        // pending entries here to avoid double-counting.
        applyLightningBoost: (
          raceId: string,
          boostedItemIds: string[],
          boostPercent: number
        ) => {
          if (!raceId || boostedItemIds.length === 0 || boostPercent <= 0) return
          const factor = 1 + boostPercent / 100
          const boostedSet = new Set(boostedItemIds)
          set(
            (state) => {
              // Remember the config so any bet arriving after this call
              // (page-reload race) can be boosted on insert by betReceived.
              state.lightningRoundConfigs[raceId] = {
                items: boostedItemIds,
                percent: boostPercent
              }
              const apply = (bag: Record<string, SocialBet>) => {
                for (const id in bag) {
                  const bet = bag[id]
                  if (!bet) continue
                  if (bet.raceId !== raceId) continue
                  if (bet.status !== 'pending') continue
                  if (state.boostedBetIds[id]) continue
                  const onBoosted = (bet.betDetails?.selectedItems ?? []).some((m) =>
                    boostedSet.has(m)
                  )
                  if (!onBoosted) continue
                  bet.potentialPayout = bet.potentialPayout * factor
                  state.boostedBetIds[id] = true
                }
              }
              apply(state.allBets)
              apply(state.previousBets)
              // `bets` is the live ticker (subset of allBets); `topBets`
              // is the persistent SocialList feed (the one the player
              // actually sees in the left panel). Both are subsets of
              // allBets, but they're stored separately, so we have to
              // rewrite them by ID from the now-boosted allBets — the
              // SocialList reads `topBets` and would otherwise keep
              // showing unboosted values even after this action runs.
              state.bets = state.bets.map((b) => state.allBets[b.id] ?? b)
              state.topBets = state.topBets.map((b) => state.allBets[b.id] ?? b)
            },
            undefined,
            'applyLightningBoost'
          )
        }
      }
    })),
    { name: 'SocialStore', enabled: isDevtoolsEnabled() }
  )
)

export const useSocialActions = () => useSocialStore((state) => state.actions)

export const useGetSocialBets = () => useSocialStore((state) => state.bets)

export const useGetSocialTopBets = () => useSocialStore((state) => state.topBets)

export const useGetTotalBets = () => useSocialStore((state) => state.totalBets)

export const useGetTotalAmount = () => useSocialStore((state) => state.totalAmount)

export const useGetSocialBetsGameId = () => useSocialStore((state) => state.gameId)

export const useGetSocialBetsRaceId = () => useSocialStore((state) => state.raceId)

/** Returns all bets for the current round as a Record<betId, SocialBet> */
export const useGetAllSocialBets = () => useSocialStore((state) => state.allBets)

/** Returns all bets from the previous round as a Record<betId, SocialBet> */
export const useGetPreviousSocialBets = () => useSocialStore((state) => state.previousBets)

/** Returns the previous round's race ID */
export const useGetPreviousRaceId = () => useSocialStore((state) => state.previousRaceId)

const updateTopBets = (newBet: SocialBet) => (state: SocialState) => {
  if (state.topBets.length < SOCIAL_CONFIG.TOP_BETS_LIMITER) {
    state.topBets.push(newBet)
    return
  }

  const minBet = state.topBets.reduce(
    (min, bet) => (bet.betAmount < min.betAmount ? bet : min),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- state.topBets is guaranteed to have at least one element here
    state.topBets[0]!
  )

  if (newBet.betAmount > minBet.betAmount) {
    state.topBets = [...state.topBets.filter((bet) => bet.id !== minBet.id), newBet]
  }
}

const resetBets = (state: SocialState) => {
  state.bets = []
  state.topBets = []
  state.totalBets = 0
  state.totalAmount = 0
  state.allBets = {}
  state.previousBets = {}
  state.previousRaceId = undefined
  state.pendingByRound = {}
}

/** Shared live-insert (v49): boost-on-insert (page-reload lightning race),
 *  id-dedupe (re-broadcast / drain-after-snapshot overlap), ticker queue,
 *  registry, top-bets. Used by betReceived's current-round path and the
 *  raceChanged drain so both behave identically. */
const insertBet = (state: SocialState, bet: SocialBet) => {
  if (state.allBets[bet.id]) return
  const config = state.lightningRoundConfigs[bet.raceId]
  const shouldBoost =
    !!config &&
    bet.status === 'pending' &&
    !state.boostedBetIds[bet.id] &&
    (bet.betDetails?.selectedItems ?? []).some((m) => config.items.includes(m))
  const insertedBet: SocialBet = shouldBoost
    ? {
        ...bet,
        potentialPayout: bet.potentialPayout * (1 + config.percent / 100)
      }
    : bet
  if (shouldBoost) {
    state.boostedBetIds[bet.id] = true
  }
  state.bets.push(insertedBet)
  state.allBets[insertedBet.id] = insertedBet
  updateTopBets(insertedBet)(state)
}

// Rounds are sequential, so anything beyond the newest few buffered ids is
// garbage from a dead round — cap the buffer (insertion order = age).
const MAX_PENDING_ROUNDS = 3
const prunePending = (state: SocialState) => {
  const keys = Object.keys(state.pendingByRound)
  for (let i = 0; i < keys.length - MAX_PENDING_ROUNDS; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- bounded by keys.length
    delete state.pendingByRound[keys[i]!]
  }
}
